import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import { FadeUp, Eyebrow } from "./Reveal";
import { useLang } from "@/i18n";

const WORKS = [
  {
    id: "ikigai",
    name: "ikigai",
    category: "Japanese Fine Dining",
    services: ["Brand Identity", "Menu Design", "Social Strategy"],
    image: "/assets/work-ikigai.jpg",
    summary:
      "Full visual identity and menu system for an upscale Japanese dining concept — from the house of Harisons Caterers, est. 1960.",
  },
  {
    id: "weirdough",
    name: "WEIRDOUGH",
    category: "Artisan Pizza Delivery",
    services: ["Rebranding", "Packaging", "Performance Ads"],
    image: "/assets/work-weirdough.jpg",
    summary:
      "A disruptive red-hot identity and menu design for a sourdough pizza movement built for delivery-first culture.",
  },
  {
    id: "ihr-essentials",
    name: "ihr essentials",
    category: "Clean Botanical Skincare",
    services: ["Content Production", "E-commerce Creative", "Social Campaigns"],
    image: "/assets/work-ihr.jpg",
    summary:
      "Botanical visual storytelling and scroll-stopping campaign assets for a clean skincare label — serums, glow and all.",
  },
  {
    id: "poc",
    name: "POC",
    category: "Contemporary Apparel",
    services: ["Ad Production", "LinkedIn & YouTube Ads", "Brand Relaunch"],
    image: "/assets/work-poc.jpg",
    summary:
      "Modernized positioning and a high-impact ad campaign for urban denim & jeggings across LinkedIn and YouTube.",
  },
];

const LIVE_SITES = [
  {
    id: "exance",
    name: "Exance",
    tag: "Website",
    url: "https://exance.co.uk/",
    image: "/assets/live-exance.jpg",
    summary: "Corporate website for a London MGA specialising in construction & financial lines insurance.",
  },
  {
    id: "compariqo",
    name: "Compariqo",
    tag: "Website",
    url: "https://compariqo.com/",
    image: "/assets/live-compariqo.jpg",
    summary: "Quote platform for property & construction insurance — instant quotes, kept simple.",
  },
  {
    id: "cubezix",
    name: "CubeZix",
    tag: "Website",
    url: "https://cubezix.com/",
    image: "/assets/live-cubezix.jpg",
    summary: "Corporate website for a Dubai IT solutions provider serving the MENA region.",
  },
];

const FINOX_SCREENS = ["/assets/finox-1.jpg", "/assets/finox-2.jpg", "/assets/finox-3.jpg"];
const FINOX_URL = "https://www.figma.com/proto/87rkXrDWB49xsGTIGlI1JW/Finox?node-id=1-847&p=f&t=13q7W5SjbGt89IWL-1&scaling=min-zoom&content-scaling=fixed&page-id=0%3A1";

const CAMPAIGNS = [
  {
    id: "meta-smb",
    name: "Meta for Business",
    motive: "Awareness",
    platform: "Instagram + Facebook",
    image: "/assets/camp-meta.jpg",
    summary:
      "Partnered with fintech creator Neha Nagar to educate and empower Indian SMBs to transition from organic content to paid Meta campaigns.",
    influencers: ["Neha Nagar"],
  },
  {
    id: "lxme-pay",
    name: "LXME Pay",
    motive: "ROI + Awareness",
    platform: "Instagram + YouTube",
    image: "/assets/camp-lxme.jpg",
    url: "https://www.instagram.com/reel/DSAJnp3iGnE/",
    linkLabel: "Watch the reel",
    summary:
      "Diwali-season UGC campaign making daily payments smarter — real-time category budgeting and a ₹45 digital-gold first-transaction reward.",
    influencers: ["Pooja Patel"],
  },
  {
    id: "growth-school",
    name: "Growth School",
    motive: "Lead Generation",
    platform: "Instagram Stories",
    image: "/assets/camp-growth.jpg",
    summary:
      "'Reply AI' DM-trigger campaign driving high-intent leads to live AI workshops and higher-ticket programs.",
    influencers: ["Harman Singh", "Harshita Gupta"],
    metrics: ["243K+ views", "211K accounts reached", "11.7K interactions"],
  },
  {
    id: "sahi",
    name: "SAHI Trading App",
    motive: "Awareness",
    platform: "LinkedIn",
    image: "/assets/camp-sahi.jpg",
    url: "https://www.linkedin.com/posts/nidhi-nagar-b74581135_finance-investing-activity-7399097726195523584-QTax",
    linkLabel: "View the post",
    summary:
      "Creator-led video content showcasing SAHI's high-performance trading features — widespread organic reach converting viewers into active app users.",
    influencers: ["Nidhi Nagar"],
  },
];

export default function Portfolio() {
  const { t } = useLang();
  const [finoxIdx, setFinoxIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setFinoxIdx((i) => (i + 1) % FINOX_SCREENS.length), 3500);
    return () => clearInterval(t);
  }, []);

  return (
    <section id="work" data-testid="portfolio-section" className="relative py-24 sm:py-36 bg-[#F0E9FF]/30 border-y border-[#2B0A4E]/8">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <FadeUp>
          <Eyebrow>{t("work_eyebrow")}</Eyebrow>
          <h2 className="mt-4 font-serif-display font-bold text-3xl sm:text-4xl lg:text-5xl text-[#2B0A4E] max-w-3xl leading-tight" data-testid="portfolio-heading">
            {t("work_h1")} <span className="italic gold-highlight">{t("work_h2")}</span>
          </h2>
        </FadeUp>

        <div className="mt-14 grid md:grid-cols-2 gap-8 lg:gap-12">
          {WORKS.map((w, i) => (
            <FadeUp key={w.id} delay={0.08 * (i % 2)} className={i % 2 === 1 ? "md:mt-16" : ""}>
              <article data-testid={`work-card-${w.id}`} className="group">
                <div className="relative overflow-hidden sketch-border bg-[#FFFFFF] p-3 sm:p-4">
                  <div className="overflow-hidden rounded-lg aspect-[4/5] sm:aspect-[5/6]">
                    <img
                      src={w.image}
                      alt={`${w.name} — ${w.category}`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                  <span className="absolute top-6 left-6 font-mono-label text-[10px] tracking-[0.2em] uppercase bg-[#FDFBF5]/90 backdrop-blur px-3 py-1.5 rounded-full border border-[#2B0A4E]/15 text-[#2B0A4E]">
                    {w.category}
                  </span>
                </div>
                <div className="mt-5 flex items-start justify-between gap-4 px-1">
                  <div>
                    <h3 className="font-serif-display font-bold text-2xl sm:text-3xl text-[#2B0A4E] group-hover:italic transition-all duration-300">
                      {w.name}
                    </h3>
                    <p className="mt-2 text-sm sm:text-base text-[#5E4783] leading-relaxed max-w-md">
                      {w.summary}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {w.services.map((s) => (
                        <span
                          key={s}
                          className="font-mono-label text-[9px] sm:text-[10px] tracking-wider uppercase bg-[#8B5CF6]/60 text-[#2B0A4E] px-2.5 py-1 rounded-full"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                  <span className="font-serif-display text-4xl text-[#8B5CF6] shrink-0" style={{ WebkitTextStroke: "1px #2B0A4E" }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
              </article>
            </FadeUp>
          ))}
        </div>
        <div className="mt-24 sm:mt-32" data-testid="campaign-case-studies">
          <FadeUp className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <Eyebrow>{t("camp_eyebrow")}</Eyebrow>
              <h2 className="mt-4 font-serif-display font-bold text-3xl sm:text-4xl lg:text-5xl text-[#2B0A4E] leading-tight" data-testid="campaigns-heading">
                {t("camp_h1")} <span className="italic gold-highlight">{t("camp_h2")}</span>
              </h2>
            </div>
            <p className="font-mono-label text-[11px] tracking-[0.2em] uppercase text-[#2B0A4E]/50">
              {t("camp_note")}
            </p>
          </FadeUp>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {CAMPAIGNS.map((c, i) => (
              <FadeUp key={c.id} delay={0.06 * i}>
                <article data-testid={`campaign-card-${c.id}`} className="group sketch-border bg-[#FFFFFF] p-3 sm:p-4 h-full flex flex-col hover:-translate-y-1.5 hover:shadow-[6px_6px_0_#8B5CF6] transition-[transform,box-shadow] duration-300">
                  <div className="overflow-hidden rounded-lg aspect-[4/5] bg-[#F0E9FF]">
                    <img
                      src={c.image}
                      alt={`${c.name} campaign creative`}
                      loading="lazy"
                      className="w-full h-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                  <div className="flex-1 px-2 pt-4">
                    <div className="flex flex-wrap gap-2">
                      <span className="font-mono-label text-[9px] tracking-[0.15em] uppercase bg-[#F2C230] text-[#2B0A4E] px-2.5 py-1 rounded-full">
                        {c.motive}
                      </span>
                      <span className="font-mono-label text-[9px] tracking-[0.15em] uppercase bg-[#8B5CF6]/15 text-[#8B5CF6] px-2.5 py-1 rounded-full">
                        {c.platform}
                      </span>
                    </div>
                    <h3 className="mt-3 font-serif-display font-bold text-xl sm:text-2xl text-[#2B0A4E]">
                      {c.name}
                    </h3>
                    <p className="mt-2 text-sm text-[#5E4783] leading-relaxed">{c.summary}</p>
                    <p className="mt-3 font-mono-label text-[9px] tracking-[0.15em] uppercase text-[#2B0A4E]/50">
                      Creators: {c.influencers.join(" · ")}
                    </p>
                    {c.url && (
                      <a
                        data-testid={`campaign-link-${c.id}`}
                        href={c.url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-3 inline-flex items-center gap-1.5 font-mono-label text-[10px] tracking-[0.15em] uppercase text-[#2B0A4E] border-b-2 border-[#F2C230] pb-0.5 hover:text-[#8B5CF6] hover:border-[#8B5CF6] transition-colors duration-300"
                      >
                        {c.linkLabel} <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                  {c.metrics && (
                    <div className="mt-4 mx-2 mb-1 flex flex-wrap gap-2">
                      {c.metrics.map((m) => (
                        <span key={m} data-testid={`campaign-metric-${c.id}`} className="font-mono-label text-[9px] tracking-[0.1em] uppercase bg-[#2B0A4E] text-[#F2C230] px-2.5 py-1.5 rounded-full">
                          {m}
                        </span>
                      ))}
                    </div>
                  )}
                </article>
              </FadeUp>
            ))}
            <FadeUp delay={0.3}>
              <button
                data-testid="campaigns-cta-card"
                onClick={() => window.__lenis?.scrollTo("#contact", { offset: -96 })}
                className="sketch-border w-full h-full min-h-[280px] bg-[#2B0A4E] text-[#FDFBF5] p-7 sm:p-8 flex flex-col justify-between text-left hover:bg-[#3E116B] hover:-translate-y-1.5 transition-[transform,background-color] duration-300"
              >
                <span className="font-mono-label text-[10px] tracking-[0.25em] uppercase text-[#F2C230]">
                  {t("campcta_top")}
                </span>
                <span className="font-serif-display italic text-2xl sm:text-3xl leading-snug">
                  {t("campcta_bottom")}
                </span>
              </button>
            </FadeUp>
          </div>
        </div>

        <div className="mt-24 sm:mt-32" data-testid="live-projects">
          <FadeUp className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <Eyebrow>{t("live_eyebrow")}</Eyebrow>
              <h2 className="mt-4 font-serif-display font-bold text-3xl sm:text-4xl lg:text-5xl text-[#2B0A4E] leading-tight" data-testid="live-heading">
                {t("live_h1")} <span className="italic gold-highlight">{t("live_h2")}</span>
              </h2>
            </div>
            <p className="font-mono-label text-[11px] tracking-[0.2em] uppercase text-[#2B0A4E]/50">
              {t("live_note")}
            </p>
          </FadeUp>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {LIVE_SITES.map((s, i) => (
              <FadeUp key={s.id} delay={0.08 * i}>
                <article data-testid={`live-card-${s.id}`} className="group sketch-border bg-[#FFFFFF] p-3 sm:p-4 h-full flex flex-col hover:-translate-y-1.5 hover:shadow-[6px_6px_0_#2B0A4E] transition-[transform,box-shadow] duration-300">
                  <div className="overflow-hidden rounded-lg aspect-video">
                    <img
                      src={s.image}
                      alt={`${s.name} website preview`}
                      loading="lazy"
                      className="w-full h-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                  <div className="flex-1 px-2 pt-4">
                    <span className="font-mono-label text-[9px] tracking-[0.2em] uppercase bg-[#8B5CF6]/15 text-[#8B5CF6] px-2.5 py-1 rounded-full">
                      {s.tag}
                    </span>
                    <h3 className="mt-3 font-serif-display font-bold text-xl sm:text-2xl text-[#2B0A4E]">
                      {s.name}
                    </h3>
                    <p className="mt-2 text-sm text-[#5E4783] leading-relaxed">{s.summary}</p>
                  </div>
                  <a
                    data-testid={`live-link-${s.id}`}
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 inline-flex items-center justify-center gap-2 sketch-border bg-[#2B0A4E] text-[#FDFBF5] px-5 py-2.5 font-mono-label text-[10px] tracking-[0.2em] uppercase hover:bg-[#3E116B] transition-colors duration-300"
                  >
                    Visit {s.name} <ExternalLink size={13} />
                  </a>
                </article>
              </FadeUp>
            ))}
          </div>

          <FadeUp delay={0.1} className="mt-8">
            <article data-testid="live-card-finox" className="sketch-border bg-[#2B0A4E] p-6 sm:p-10 grid md:grid-cols-2 gap-8 items-center overflow-hidden">
              <div className="relative mx-auto w-full max-w-[260px]">
                <div className="rounded-[2rem] border-4 border-[#F2C230]/80 bg-black overflow-hidden aspect-[9/16] relative shadow-[0_20px_50px_rgba(0,0,0,0.4)]">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={finoxIdx}
                      src={FINOX_SCREENS[finoxIdx]}
                      alt={`Finox app screen ${finoxIdx + 1}`}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -30 }}
                      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                      className="absolute inset-0 w-full h-full object-cover"
                      draggable="false"
                    />
                  </AnimatePresence>
                </div>
                <button
                  data-testid="finox-carousel-prev"
                  onClick={() => setFinoxIdx((finoxIdx - 1 + FINOX_SCREENS.length) % FINOX_SCREENS.length)}
                  className="absolute top-1/2 -translate-y-1/2 -left-4 sm:-left-6 w-9 h-9 rounded-full bg-[#F2C230] text-[#2B0A4E] flex items-center justify-center hover:scale-110 transition-transform"
                  aria-label="Previous screen"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  data-testid="finox-carousel-next"
                  onClick={() => setFinoxIdx((finoxIdx + 1) % FINOX_SCREENS.length)}
                  className="absolute top-1/2 -translate-y-1/2 -right-4 sm:-right-6 w-9 h-9 rounded-full bg-[#F2C230] text-[#2B0A4E] flex items-center justify-center hover:scale-110 transition-transform"
                  aria-label="Next screen"
                >
                  <ChevronRight size={18} />
                </button>
                <div className="mt-4 flex justify-center gap-2">
                  {FINOX_SCREENS.map((_, i) => (
                    <button
                      key={i}
                      data-testid={`finox-carousel-dot-${i}`}
                      onClick={() => setFinoxIdx(i)}
                      aria-label={`Go to screen ${i + 1}`}
                      className={`h-2 rounded-full transition-all duration-300 ${i === finoxIdx ? "w-6 bg-[#F2C230]" : "w-2 bg-[#FDFBF5]/30"}`}
                    />
                  ))}
                </div>
              </div>
              <div>
                <span className="font-mono-label text-[9px] tracking-[0.2em] uppercase bg-[#F2C230] text-[#2B0A4E] px-2.5 py-1 rounded-full">
                  App prototype
                </span>
                <h3 className="mt-4 font-serif-display font-bold text-2xl sm:text-3xl text-[#FDFBF5]">
                  Finox
                </h3>
                <p className="mt-3 text-sm sm:text-base text-[#FDFBF5]/75 leading-relaxed max-w-md">
                  A mobile finance app designed end-to-end — from splash to onboarding and
                  account flows — as a fully interactive Figma prototype.
                </p>
                <a
                  data-testid="live-link-finox"
                  href={FINOX_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-6 inline-flex items-center gap-2 sketch-border bg-[#F2C230] text-[#2B0A4E] px-6 py-3 font-mono-label text-[10px] tracking-[0.2em] uppercase hover:bg-[#FDFBF5] transition-colors duration-300"
                >
                  View prototype <ExternalLink size={13} />
                </a>
              </div>
            </article>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
